import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useStudents } from "../../../hooks/useStudents";
import { getMarksheetsByClassKeyAndSessionId, useMarkSheets } from "../../../hooks/useMarkSheets";
import { useSchools } from "../../../hooks/useSchools";
import SchoolRegisterTemplate11thClass from "../../../components/templates/SchoolRegisterTemplate11thClass";
import SchoolRegisterTemplate9thClass from "../../../components/templates/SchoolRegisterTemplate9thClass";
import { useQuery } from "@tanstack/react-query";

// Helper function to check if class is 9th or 11th
const isSchoolRegisterClass = (className?: string | null): boolean => {
  if (!className) return false;
  const normalized = className.toLowerCase();
  const numbers = new Set((normalized.match(/\d+/g) || []).map((num) => Number.parseInt(num, 10)));
  const hasNumber = (n: number) => numbers.has(n);
  return (
    hasNumber(9) ||
    hasNumber(11) ||
    normalized.includes('class 9') ||
    normalized.includes('class 11') ||
    normalized.includes('9th') ||
    normalized.includes('11th') ||
    normalized.includes('ninth') ||
    normalized.includes('eleventh')
  );
};

const isNinthClass = (className?: string | null): boolean => {
  if (!className) return false;
  const normalized = className.toLowerCase();
  const numbers = new Set((normalized.match(/\d+/g) || []).map((num) => Number.parseInt(num, 10)));
  return (
    numbers.has(9) ||
    normalized.includes('class 9') ||
    normalized.includes('9th') ||
    normalized.includes('ninth')
  );
};

const isEleventhClass = (className?: string | null): boolean => {
  if (!className) return false;
  const normalized = className.toLowerCase();
  const numbers = new Set((normalized.match(/\d+/g) || []).map((num) => Number.parseInt(num, 10)));
  return (
    numbers.has(11) ||
    normalized.includes('class 11') ||
    normalized.includes('11th') ||
    normalized.includes('eleventh')
  );
};

const SchoolRegisterManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedClass, selectedSession } = useSessionClass();
  const schoolId = user?.schoolId;
  const rawClassId = selectedClass?.id;

  const classNameFilter =
    selectedClass?.section === "__CLASS_ALL__"
      ? "Class-ALL"
      : selectedClass?.section === "__GRADE_ALL__"
        ? `${String(selectedClass?.name || "").trim()}-ALL`
        : (String(selectedClass?.name || "").includes("-ALL") ? String(selectedClass?.name || "") : null);

  const classId =
    typeof rawClassId === "number" && rawClassId > 0 && !classNameFilter ? rawClassId : undefined;

  const [registerModalOpen, setRegisterModalOpen] = useState(false);

  // Check if selected class is 9th or 11th
  const isAllowedClass = useMemo(() => {
    return isSchoolRegisterClass(selectedClass?.name);
  }, [selectedClass?.name]);

  // Fetch school data
  const { useSchoolById } = useSchools(false, { enableQuery: false });
  const { data: schoolData } = useSchoolById(schoolId ?? undefined);

  // Fetch students for the selected class and session
  const { useStudentsBySessionIdAndClassId, useStudentsBySessionIdAndClassName } = useStudents(
    false,
    schoolId ?? undefined
  );
  const studentsByIdQuery = useStudentsBySessionIdAndClassId(selectedSession?.id, classId);
  const studentsByNameQuery = useStudentsBySessionIdAndClassName(selectedSession?.id, classNameFilter || undefined);
  const studentsQuery = classNameFilter ? studentsByNameQuery : studentsByIdQuery;
  const filteredStudents = studentsQuery.data || [];

  // Fetch marksheets
  // Important: don't let `useMarkSheets` auto-run paged marksheets queries in School Register.
  // We'll fetch marksheets via a single non-paged request below.
  useMarkSheets(
    false,
    true,
    classNameFilter ? undefined : (classId ?? null),
    selectedSession?.id,
    undefined,
    undefined,
    false,
    classNameFilter ? classNameFilter : undefined
  );

  const marksheetsClassKeyQuery = useQuery({
    queryKey: ["marksheets", "schoolRegister", classId ?? null, classNameFilter ?? null, selectedSession?.id ?? null],
    queryFn: () =>
      getMarksheetsByClassKeyAndSessionId({
        ...(classNameFilter ? { className: classNameFilter } : { classId: Number(classId) }),
        sessionId: Number(selectedSession?.id),
      }),
    enabled: !!selectedSession?.id && (!!classNameFilter || (typeof classId === "number" && classId > 0)),
    refetchOnWindowFocus: false,
  });

  const allMarksheets = Array.isArray(marksheetsClassKeyQuery.data) ? marksheetsClassKeyQuery.data : [];

  // Prepare students data for the template
  const prepareStudentsData = () => {
    // Sort students by name
    const sortedStudents = [...filteredStudents].sort((a, b) =>
      (a.studentName || "").localeCompare(b.studentName || "")
    );

    // Pre-filter marksheets for the current class and session for performance
    const sessionMarksheets = allMarksheets.filter((ms: any) => {
      const sessionOk = ms.sessionId === selectedSession?.id || ms.sessionYear === selectedSession?.name;
      if (!sessionOk) return false;
      // For normal class selection, keep strict classId match.
      if (!classNameFilter) return ms.classId === classId;
      // For *-ALL selection, marksheets are already fetched by className on the backend.
      return true;
    });

    return sortedStudents.map((student, idx) => {
      // Find marksheets for this specific student
      const studentMarksheets = sessionMarksheets.filter((ms: any) =>
        ms.student?.id === student.id || ms.studentId === student.id
      );

      // Group marksheets by type
      const quarterlyMS = studentMarksheets.find((ms: any) => ms.marksheetType === "QUARTERLY");
      const halfYearlyMS = studentMarksheets.find((ms: any) => ms.marksheetType === "HALF_YEARLY");
      const annualMS = studentMarksheets.find((ms: any) => ms.marksheetType === "ANNUAL");
      const finalMS = studentMarksheets.find((ms: any) => ms.marksheetType === "FINAL");

      // Collect all unique subjects across all marksheets for this student
      const subjectMap = new Map();
      studentMarksheets.forEach((ms: any) => {
        ms.subjects?.forEach((subj: any) => {
          const code = subj.subjectCode || subj.subjectName;
          if (code && !subjectMap.has(code)) {
            subjectMap.set(code, {
              subjectCode: subj.subjectCode,
              subjectName: subj.subjectName,
            });
          }
        });
      });

      // If no marksheets found, still try to show students if they have subjects directly (though usually they don't)
      const subjects = Array.from(subjectMap.entries()).map(([key, subjBase]) => {
        const findSubj = (ms: any) =>
          ms?.subjects?.find((s: any) => (s.subjectCode || s.subjectName) === key);

        const qSubj = findSubj(quarterlyMS) as any;
        const hSubj = findSubj(halfYearlyMS) as any;
        const aSubj = findSubj(annualMS) as any;
        const fSubj = findSubj(finalMS) as any;

        // Max/Min marks usually come from any marksheet that has them, preferably ANNUAL or FINAL
        const sourceSubj = fSubj || aSubj || hSubj || qSubj;

        return {
          subjectCode: subjBase.subjectCode,
          subjectName: subjBase.subjectName,

          maxTheory: sourceSubj?.maxTheoryMarks ?? 75,
          maxPractical: sourceSubj?.maxPracticalMarks ?? 25,
          minTheory: sourceSubj?.minTheoryMarks ?? 25,
          minPractical: sourceSubj?.minPracticalMarks ?? 8,

          // Original marks - ensure null becomes undefined
          quarterlyTheory: qSubj?.theoryMarks ?? fSubj?.quarterlyTheoryMarks ?? undefined,
          quarterlyPractical: qSubj?.practicalMarks ?? fSubj?.quarterlyPracticalMarks ?? undefined,

          halfYearlyTheory: hSubj?.theoryMarks ?? fSubj?.halfYearlyTheoryMarks ?? undefined,
          halfYearlyPractical: hSubj?.practicalMarks ?? fSubj?.halfYearlyPracticalMarks ?? undefined,

          annualTheory: aSubj?.theoryMarks ?? fSubj?.theoryMarks ?? undefined,
          annualPractical: aSubj?.practicalMarks ?? fSubj?.practicalMarks ?? undefined,
          originalPracticalMarks: fSubj?.originalPracticalMarks ?? aSubj?.originalPracticalMarks ?? undefined,

          // Weightages (Expected by SchoolRegisterTemplate)
          quarterlyWeightageTH: fSubj?.quarterlyWeightage ?? undefined,
          quarterlyWeightagePR: fSubj?.quarterlyWeightage ?? undefined,

          halfYearlyWeightageTH: fSubj?.halfYearlyWeightage ?? undefined,
          halfYearlyWeightagePR: fSubj?.halfYearlyWeightage ?? undefined,

          annualWeightageTH: aSubj?.theoryMarks ?? undefined,
          annualWeightagePR: aSubj?.practicalMarks ?? undefined,

          totalWeightageTH: fSubj?.theoryMarks ?? undefined,
          totalWeightagePR: fSubj?.practicalMarks ?? undefined,

          totalMarks: fSubj?.totalMarks || (fSubj ? (fSubj.theoryMarks || 0) + (fSubj.practicalMarks || 0) : undefined),
          remark: fSubj?.remark || aSubj?.remark,
        };
      });

      return {
        serialNumber: idx + 1,
        rollNumber: student.rollNumber || "",
        enrolmentNo: student.enrolmentNo || "",
        scholarNo: student.scholarNo || "",
        medium: student.medium || "",
        studentName: (student.studentName || "").trim(),
        fatherName: student.fatherName || "",
        motherName: student.motherName || "",
        dateOfBirth: student.dateOfBirth
          ? Array.isArray(student.dateOfBirth)
            ? `${String(student.dateOfBirth[2] || "").padStart(2, "0")}/${String(student.dateOfBirth[1] || "").padStart(2, "0")}/${student.dateOfBirth[0] || ""}`
            : student.dateOfBirth
          : "",
        result: finalMS?.result || "",
        percentGrade: finalMS?.percentage ? `${finalMS.percentage}%` : "",
        subjects: subjects,
      };
    });
  };

  const handleOpenRegister = () => {
    if (!selectedClass || !selectedSession) {
      alert(t("please_select_class_and_session") || "Please select class and session first");
      return;
    }
    if (!isAllowedClass) {
      alert(t("school_register_only_for_9th_11th") || "School Register is only available for 9th and 11th classes");
      return;
    }
    setRegisterModalOpen(true);
  };

  const handleCloseRegister = () => {
    setRegisterModalOpen(false);
  };

  const academicYear = selectedSession?.name || "2024-25";
  const className = classNameFilter || selectedClass?.name || "";

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("school_register_management")}
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" },
            }}
            onClick={handleOpenRegister}
            disabled={!selectedClass || !selectedSession || !isAllowedClass}
          >
            {t("get_school_register")}
          </Button>
        </Box>

        {studentsQuery.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {String(
              studentsQuery.error instanceof Error
                ? studentsQuery.error.message
                : t("an_error_occurred")
            )}
          </Alert>
        )}

        {!selectedClass || !selectedSession ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("please_select_class_and_session") ||
              "Please select a class and session to view school register"}
          </Alert>
        ) : !isAllowedClass ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("school_register_only_for_9th_11th") ||
              "School Register is only available for 9th and 11th classes. Please select a 9th or 11th class."}
          </Alert>
        ) : null}
      </Paper>

      {/* School Register Dialog */}
      <Dialog
        open={registerModalOpen}
        onClose={handleCloseRegister}
        maxWidth="xl"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "white",
            flexShrink: 0,
          }}
        >
          {t("school_register")}
          <IconButton onClick={handleCloseRegister} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box
          p={2}
          sx={{
            overflow: "auto",
            flex: 1,
          }}
        >
          {isNinthClass(selectedClass?.name) ? (
            <SchoolRegisterTemplate9thClass
              students={prepareStudentsData()}
              school={schoolData}
              district={schoolData?.district || ""}
              block={schoolData?.blockName || ""}
              diseCode={schoolData?.diseCode || ""}
              mpbseCode={schoolData?.schoolCode || ""}
              academicYear={academicYear}
              className={className}
              classId={classId}
            />
          ) : (
            <SchoolRegisterTemplate11thClass
              students={prepareStudentsData()}
              school={schoolData}
              district={schoolData?.district || ""}
              block={schoolData?.blockName || ""}
              diseCode={schoolData?.diseCode || ""}
              mpbseCode={schoolData?.schoolCode || ""}
              academicYear={academicYear}
              className={className}
              classId={classId}
            />
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default SchoolRegisterManagement;
